/**
 * PrintPreview
 *
 * Step 2 of the Print Wizard.
 * Shows label previews, printer selection, transport selection,
 * and executes the print job.
 */
import {
  Bluetooth,
  ContentCopy,
  Language,
  Print as PrintIcon,
  Refresh,
  Usb,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { usePrinter } from '../../hooks/usePrintJobs';
import { generatePreviewElements, generateZPLForBatch } from '../../services/printing';
import type { LabelTemplate, PrintAssetSelection, PrintTransportType } from '../../types/printing';
import { LabelPreviewRenderer } from './LabelPreviewRenderer';

const transportIcons: Record<PrintTransportType, React.ReactNode> = {
  usb: <Usb />,
  bluetooth: <Bluetooth />,
  network: <Language />,
  clipboard: <ContentCopy />,
};

const transportLabels: Record<PrintTransportType, string> = {
  usb: 'USB (WebUSB)',
  bluetooth: 'Bluetooth',
  network: 'Network (TCP/IP)',
  clipboard: 'Copy ZPL to Clipboard',
};

interface PrintPreviewProps {
  selections: PrintAssetSelection[];
  template: LabelTemplate;
  companyName: string;
  onBack: () => void;
  onComplete: (jobId: string) => void;
}

export function PrintPreview({
  selections,
  template,
  companyName,
  onBack,
  onComplete,
}: PrintPreviewProps) {
  const { user } = useAuth();
  const {
    printers,
    selectedPrinter,
    setSelectedPrinter,
    availableTransports,
    printing,
    lastError,
    scanForPrinters,
    executePrintJob,
  } = usePrinter();

  const [transport, setTransport] = useState<PrintTransportType>(
    availableTransports[0] || 'clipboard'
  );
  const [networkAddress, setNetworkAddress] = useState('');
  const [networkPort, setNetworkPort] = useState('9100');
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const totalLabels = selections.reduce((sum, s) => sum + s.quantity, 0);

  const handlePrint = useCallback(async () => {
    setResult(null);

    const job = await executePrintJob({
      selections,
      template,
      transport,
      printerAddress: transport === 'network' ? `${networkAddress}:${networkPort}` : undefined,
      printedBy: user?.full_name || user?.email || 'Unknown',
      companyName,
    });

    if (job) {
      if (job.status === 'completed') {
        setResult({
          success: true,
          message: `Print job ${job.job_number} completed. ${job.total_labels} label(s) sent.`,
        });
        onComplete(job.id);
      } else {
        setResult({
          success: false,
          message: job.error_message || 'Print job failed',
        });
      }
    }
  }, [
    selections,
    template,
    transport,
    networkAddress,
    networkPort,
    user,
    companyName,
    executePrintJob,
    onComplete,
  ]);

  const handleCopyZPL = useCallback(() => {
    const { fullZpl } = generateZPLForBatch(selections, template, {
      printedBy: user?.full_name || user?.email || 'Unknown',
      companyName,
    });
    navigator.clipboard.writeText(fullZpl).then(() => {
      setResult({ success: true, message: 'ZPL copied to clipboard' });
    });
  }, [selections, template, user, companyName]);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Print Preview & Send
      </Typography>

      {result && (
        <Alert
          severity={result.success ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setResult(null)}
        >
          {result.message}
        </Alert>
      )}

      {lastError && !result && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {lastError}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Printer & transport settings */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Print Settings
              </Typography>

              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Print Method</InputLabel>
                  <Select
                    value={transport}
                    label="Print Method"
                    onChange={e => setTransport(e.target.value as PrintTransportType)}
                  >
                    {availableTransports.map(t => (
                      <MenuItem key={t} value={t}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {transportIcons[t]}
                          <span>{transportLabels[t]}</span>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {transport === 'network' && (
                  <>
                    <TextField
                      label="Printer IP Address"
                      value={networkAddress}
                      onChange={e => setNetworkAddress(e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="192.168.1.100"
                    />
                    <TextField
                      label="Port"
                      value={networkPort}
                      onChange={e => setNetworkPort(e.target.value)}
                      size="small"
                      fullWidth
                      type="number"
                    />
                  </>
                )}

                {(transport === 'usb' || transport === 'bluetooth') && (
                  <>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {printers.length > 0
                          ? `${printers.length} printer(s) found`
                          : 'No printers detected'}
                      </Typography>
                      <Button size="small" startIcon={<Refresh />} onClick={scanForPrinters}>
                        Scan
                      </Button>
                    </Stack>

                    {printers.length > 0 && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Printer</InputLabel>
                        <Select
                          value={selectedPrinter?.id || ''}
                          label="Printer"
                          onChange={e => {
                            const p = printers.find(pr => pr.id === e.target.value);
                            if (p) setSelectedPrinter(p);
                          }}
                        >
                          {printers.map(p => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Template
                  </Typography>
                  <Typography variant="body2">{template.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Label Size
                  </Typography>
                  <Typography variant="body2">
                    {(template.width_dots / 203).toFixed(2)}&quot; x{' '}
                    {(template.height_dots / 203).toFixed(2)}&quot; ({template.width_dots}x
                    {template.height_dots} dots)
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Labels
                  </Typography>
                  <Typography variant="body2">
                    {totalLabels} ({selections.length} unique asset
                    {selections.length !== 1 ? 's' : ''})
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Label previews */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Label Previews
              </Typography>
            </CardContent>
            <List sx={{ maxHeight: 500, overflow: 'auto', px: 2, pb: 2 }}>
              {selections.map(sel => {
                const elements = generatePreviewElements(sel.asset, template, {
                  printedBy: user?.full_name || user?.email || 'Unknown',
                  companyName,
                });

                return (
                  <ListItem
                    key={sel.asset.id}
                    sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 1 }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                      <Paper elevation={2} sx={{ p: 0.5 }}>
                        <LabelPreviewRenderer
                          elements={elements}
                          widthDots={template.width_dots}
                          heightDots={template.height_dots}
                          scale={0.4}
                        />
                      </Paper>
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {sel.asset.serial_number || sel.asset.tag_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sel.asset.type || 'No type'} &bull;{' '}
                          {sel.asset.customer?.name || 'No customer'}
                        </Typography>
                        <Chip
                          label={`x${sel.quantity}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ alignSelf: 'flex-start' }}
                        />
                      </Stack>
                    </Stack>
                  </ListItem>
                );
              })}
            </List>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" justifyContent="space-between">
        <Button variant="outlined" onClick={onBack} disabled={printing}>
          Back
        </Button>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopyZPL}
            disabled={printing}
          >
            Copy ZPL
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={
              printing ||
              (transport === 'network' && !networkAddress) ||
              (transport === 'usb' && printers.length === 0 && !('usb' in navigator))
            }
            size="large"
          >
            {printing ? 'Printing...' : `Print ${totalLabels} Label${totalLabels !== 1 ? 's' : ''}`}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
